import { formatDistanceToNow } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Handshake,
  Heart,
  Lightbulb,
  MessageCircle,
  Mic,
  Music,
  Play,
  RefreshCw,
  Users,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Image, Linking, Platform, Pressable, StyleSheet } from 'react-native';
import { InstagramIcon } from '../../../components/icons/BrandIcons';
import { Badge, Box, Card, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import type {
  ClientProfileView,
  InstagramRecommendationsView,
  RecommendationView,
} from '../utils/instagramRecommendations';
import type { InstagramReelView } from '../utils/instagramReels';
import { CommentRow } from './CommentRow';
import type { LinkedInPostData } from './LinkedInPostsCard';
import { EngagementDisclosure, EngagementPanel, EnrichButton } from './PostEngagement';

type ContentTab = 'posts' | 'reels' | 'analysis';

interface InstagramContentCarouselCardProps {
  readonly posts: LinkedInPostData[];
  readonly reels: InstagramReelView[];
  readonly recommendations?: InstagramRecommendationsView | null;
  readonly onRefresh?: () => void;
  readonly isRefreshing?: boolean;
  readonly refreshStatus?: 'updated' | 'no_changes' | 'error' | null;
  readonly postsLastRefreshedAt?: string;
  readonly onEnrichCommenter?: (profileUrl: string, name: string) => void;
}

/**
 * FLOSEAI-625 — Instagram content, one piece at a time.
 *
 * Instagram used to render through `LinkedInPostsCard`, which stacks every post
 * vertically: ten scraped posts meant scrolling all ten to reach the analysis
 * below. This paginates instead, and splits posts from reels because they read
 * differently — a reel carries a transcript and a view count, a post does not.
 *
 * The reels tab also fixes a gap: the pipeline had been persisting reels with
 * their transcripts and nothing in the UI ever read them.
 */
export function InstagramContentCarouselCard({
  posts,
  reels,
  recommendations,
  onRefresh,
  isRefreshing,
  refreshStatus,
  postsLastRefreshedAt,
  onEnrichCommenter,
}: InstagramContentCarouselCardProps) {
  const { t, language } = useTranslation();
  const hasPosts = posts.length > 0;
  const hasReels = reels.length > 0;
  const hasAnalysis = Boolean(recommendations);

  // Open on whichever tab actually has content, so the card never opens empty.
  const [tab, setTab] = useState<ContentTab>(() => {
    if (hasPosts) return 'posts';
    return hasReels ? 'reels' : 'analysis';
  });
  const [index, setIndex] = useState(0);

  // The analysis tab is a single page of prose, so it has nothing to paginate.
  const items = (() => {
    if (tab === 'posts') return posts;
    return tab === 'reels' ? reels : [];
  })();
  const total = items.length;

  // Switching tabs must reset the cursor — page 7 of the posts tab is not a
  // meaningful position in a reels tab that only has 3.
  useEffect(() => {
    setIndex(0);
  }, [tab]);

  if (!hasPosts && !hasReels && !hasAnalysis) return null;

  const current = items[Math.min(index, Math.max(total - 1, 0))];

  return (
    <Card padding="lg" radius="xl">
      <VStack spacing="md">
        <HStack spacing="sm" align="center" justify="between">
          <HStack spacing="sm" align="center">
            <InstagramIcon size={18} color={colors.brand.instagram} strokeWidth={2} />
            <Text variant="h6" color={colors.text.primary} style={styles.semibold}>
              {t('leads.instagramContent.title')}
            </Text>
          </HStack>
          {tab !== 'analysis' && total > 1 && (
            <Pager
              index={index}
              total={total}
              onChange={setIndex}
              prevLabel={t('leads.instagramContent.previous')}
              nextLabel={t('leads.instagramContent.next')}
            />
          )}
        </HStack>

        {[hasPosts, hasReels, hasAnalysis].filter(Boolean).length > 1 && (
          <HStack spacing="xs" align="center" style={styles.wrap}>
            {hasPosts && (
              <TabButton
                label={`${t('leads.instagramContent.posts')} (${posts.length})`}
                active={tab === 'posts'}
                onPress={() => setTab('posts')}
              />
            )}
            {hasReels && (
              <TabButton
                label={`${t('leads.instagramContent.reels')} (${reels.length})`}
                active={tab === 'reels'}
                onPress={() => setTab('reels')}
              />
            )}
            {hasAnalysis && (
              <TabButton
                label={t('leads.instagramContent.analysis')}
                active={tab === 'analysis'}
                onPress={() => setTab('analysis')}
              />
            )}
          </HStack>
        )}

        {tab === 'analysis' && recommendations && (
          <AnalysisPage recommendations={recommendations} t={t} />
        )}

        {tab !== 'analysis' &&
          current &&
          (tab === 'posts' ? (
            <PostPage
              post={current as LinkedInPostData}
              language={language}
              t={t}
              onEnrichCommenter={onEnrichCommenter}
            />
          ) : (
            <ReelPage reel={current as InstagramReelView} language={language} t={t} />
          ))}

        {tab !== 'analysis' && total > 1 && (
          <Dots total={total} index={index} onSelect={setIndex} />
        )}

        {onRefresh && (
          <RefreshRow
            onRefresh={onRefresh}
            isRefreshing={Boolean(isRefreshing)}
            refreshStatus={refreshStatus}
            postsLastRefreshedAt={postsLastRefreshedAt}
            language={language}
            t={t}
          />
        )}
      </VStack>
    </Card>
  );
}

/**
 * Preserves the re-scrape affordance the flat list had. Sits at the bottom so it
 * never competes with the content for attention.
 */
function RefreshRow({
  onRefresh,
  isRefreshing,
  refreshStatus,
  postsLastRefreshedAt,
  language,
  t,
}: Readonly<{
  onRefresh: () => void;
  isRefreshing: boolean;
  refreshStatus?: 'updated' | 'no_changes' | 'error' | null;
  postsLastRefreshedAt?: string;
  language: string;
  t: (key: string) => string;
}>) {
  const lastRefreshed = postsLastRefreshedAt ? relativeDate(postsLastRefreshedAt, language) : null;

  const statusLabel = (() => {
    if (isRefreshing) return t('leads.instagramContent.refreshing');
    if (refreshStatus === 'updated') return t('leads.instagramContent.refreshUpdated');
    if (refreshStatus === 'no_changes') return t('leads.instagramContent.refreshNoChanges');
    if (refreshStatus === 'error') return t('leads.instagramContent.refreshError');
    return lastRefreshed;
  })();

  return (
    <HStack spacing="sm" align="center" justify="between">
      {statusLabel ? (
        <Text
          variant="caption"
          color={refreshStatus === 'error' ? colors.status.error.main : colors.text.tertiary}
        >
          {statusLabel}
        </Text>
      ) : (
        <Box />
      )}
      <Pressable
        onPress={onRefresh}
        disabled={isRefreshing}
        accessibilityRole="button"
        accessibilityLabel={t('leads.instagramContent.refresh')}
        accessibilityState={{ disabled: isRefreshing, busy: isRefreshing }}
        hitSlop={6}
      >
        <HStack spacing="xxs" align="center">
          <RefreshCw
            size={12}
            color={isRefreshing ? colors.text.tertiary : colors.text.interactive}
            strokeWidth={1.8}
          />
          <Text
            variant="caption"
            color={isRefreshing ? colors.text.tertiary : colors.text.interactive}
          >
            {t('leads.instagramContent.refresh')}
          </Text>
        </HStack>
      </Pressable>
    </HStack>
  );
}

function Pager({
  index,
  total,
  onChange,
  prevLabel,
  nextLabel,
}: Readonly<{
  index: number;
  total: number;
  onChange: (next: number) => void;
  prevLabel: string;
  nextLabel: string;
}>) {
  // Wraps in both directions: at the last item "next" returns to the first,
  // which is less annoying than a dead arrow when someone is skimming.
  return (
    <HStack spacing="xs" align="center">
      <Pressable
        onPress={() => onChange((index - 1 + total) % total)}
        accessibilityRole="button"
        accessibilityLabel={prevLabel}
        hitSlop={8}
      >
        <ChevronLeft size={18} color={colors.text.secondary} strokeWidth={2} />
      </Pressable>
      <Text variant="caption" color={colors.text.secondary} style={styles.counter}>
        {index + 1}/{total}
      </Text>
      <Pressable
        onPress={() => onChange((index + 1) % total)}
        accessibilityRole="button"
        accessibilityLabel={nextLabel}
        hitSlop={8}
      >
        <ChevronRight size={18} color={colors.text.secondary} strokeWidth={2} />
      </Pressable>
    </HStack>
  );
}

function TabButton({
  label,
  active,
  onPress,
}: Readonly<{ label: string; active: boolean; onPress: () => void }>) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      accessibilityLabel={label}
    >
      <Box
        paddingX="md"
        paddingY="xs"
        borderRadius="full"
        backgroundColor={active ? colors.primary.muted : colors.background.secondary}
        borderWidth={1}
        borderColor={active ? colors.primary[500] : colors.border.subtle}
      >
        <Text
          variant="caption"
          color={active ? colors.primary[500] : colors.text.secondary}
          style={active ? styles.semibold : undefined}
        >
          {label}
        </Text>
      </Box>
    </Pressable>
  );
}

function Dots({
  total,
  index,
  onSelect,
}: Readonly<{ total: number; index: number; onSelect: (next: number) => void }>) {
  // Above ~12 pieces a dot row stops being scannable and the counter carries the
  // position instead.
  if (total > 12) return null;

  return (
    <HStack spacing="xs" align="center" justify="center">
      {Array.from({ length: total }, (_, i) => (
        <Pressable
          key={i}
          onPress={() => onSelect(i)}
          accessibilityRole="button"
          accessibilityLabel={`${i + 1}`}
          hitSlop={6}
        >
          <Box style={[styles.dot, i === index ? styles.dotActive : styles.dotIdle]} />
        </Pressable>
      ))}
    </HStack>
  );
}

function openUrl(url: string) {
  if (!url) return;
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(url);
  }
}

function relativeDate(timestamp: string, language: string): string | null {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  const locale = DATE_LOCALES[language as keyof typeof DATE_LOCALES];
  return formatDistanceToNow(parsed, { addSuffix: true, ...(locale && { locale }) });
}

function Metric({ icon, value }: Readonly<{ icon: React.ReactNode; value: string | number }>) {
  return (
    <HStack spacing="xxs" align="center">
      {icon}
      <Text variant="caption" color={colors.text.tertiary}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Text>
    </HStack>
  );
}

function PostPage({
  post,
  language,
  t,
  onEnrichCommenter,
}: Readonly<{
  post: LinkedInPostData;
  language: string;
  t: (key: string) => string;
  onEnrichCommenter?: (profileUrl: string, name: string) => void;
}>) {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const posted = relativeDate(post.postedAt, language);
  const image = post.images?.[0];
  const comments = post.comments ?? [];

  return (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center" justify="between">
        <Badge variant="subtle" color="purple" size="sm">
          {t('leads.instagramContent.postBadge')}
        </Badge>
        {posted && (
          <Text variant="caption" color={colors.text.tertiary}>
            {posted}
          </Text>
        )}
      </HStack>

      {image && <Image source={{ uri: image }} style={styles.media} resizeMode="cover" />}

      {post.text ? (
        <Text variant="bodySmall" color={colors.text.secondary} style={styles.body}>
          {post.text}
        </Text>
      ) : (
        <Text variant="caption" color={colors.text.tertiary}>
          {t('leads.instagramContent.noCaption')}
        </Text>
      )}

      <HStack spacing="md" align="center">
        <Metric
          icon={<Heart size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
          value={post.numLikes}
        />
        <Metric
          icon={<MessageCircle size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
          value={post.numComments}
        />
        {post.url && <OpenLink url={post.url} label={t('leads.instagramContent.open')} />}
      </HStack>

      {/* Collapsed by design: the point of this card is less scrolling, so the
          comment thread only costs height once someone asks for it. */}
      {comments.length > 0 && (
        <>
          <EngagementDisclosure
            label={`${comments.length} ${t('leads.instagramContent.comments')}`}
            faces={comments.map((c) => ({ name: c.authorName, imageUrl: c.authorProfilePicture }))}
            expanded={commentsOpen}
            onToggle={() => setCommentsOpen((open) => !open)}
          />
          {commentsOpen && (
            <EngagementPanel>
              {comments.map((comment, i) => (
                <CommentRow
                  key={`${comment.authorProfileUrl ?? comment.authorName}-${i}`}
                  authorName={comment.authorName}
                  authorImageUrl={comment.authorProfilePicture}
                  body={comment.text}
                  action={
                    onEnrichCommenter && comment.authorProfileUrl ? (
                      <EnrichButton
                        profileUrl={comment.authorProfileUrl}
                        name={comment.authorName}
                        onEnrich={onEnrichCommenter}
                      />
                    ) : undefined
                  }
                />
              ))}
            </EngagementPanel>
          )}
        </>
      )}
    </VStack>
  );
}

function ReelPage({
  reel,
  language,
  t,
}: Readonly<{ reel: InstagramReelView; language: string; t: (key: string) => string }>) {
  const posted = relativeDate(reel.timestamp, language);

  return (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center" justify="between">
        <Badge variant="subtle" color="blue" size="sm">
          {t('leads.instagramContent.reelBadge')}
        </Badge>
        {posted && (
          <Text variant="caption" color={colors.text.tertiary}>
            {posted}
          </Text>
        )}
      </HStack>

      {reel.displayUrl && (
        <Image source={{ uri: reel.displayUrl }} style={styles.media} resizeMode="cover" />
      )}

      {reel.caption ? (
        <Text variant="bodySmall" color={colors.text.secondary} style={styles.body}>
          {reel.caption}
        </Text>
      ) : null}

      {/* The transcript is the whole reason reels are scraped: it is the person
          speaking, at length, in their own words. */}
      {reel.transcript && (
        <Box backgroundColor={colors.background.secondary} borderRadius="md" padding="md">
          <HStack spacing="xs" align="center" style={{ marginBottom: 6 }}>
            <Mic size={12} color={colors.accent.purple[400]} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.tertiary} style={styles.semibold}>
              {t('leads.instagramContent.transcript')}
            </Text>
          </HStack>
          <Text variant="caption" color={colors.text.secondary} style={styles.transcript}>
            {reel.transcript}
          </Text>
        </Box>
      )}

      <HStack spacing="md" align="center" style={styles.wrap}>
        <Metric
          icon={<Heart size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
          value={reel.likesCount}
        />
        <Metric
          icon={<MessageCircle size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
          value={reel.commentsCount}
        />
        {reel.viewCount !== undefined && (
          <Metric
            icon={<Play size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
            value={reel.viewCount}
          />
        )}
        {reel.durationSeconds !== undefined && (
          <Metric
            icon={<Clock size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
            value={`${Math.round(reel.durationSeconds)}s`}
          />
        )}
        {reel.music && (
          <Metric
            icon={<Music size={12} color={colors.text.tertiary} strokeWidth={1.8} />}
            value={reel.music}
          />
        )}
        {reel.url && <OpenLink url={reel.url} label={t('leads.instagramContent.open')} />}
      </HStack>
    </VStack>
  );
}

/**
 * What to actually talk to this account about: who their own customers are, what
 * they could publish next, and what we could offer them. All of it derived from
 * the content in the other two tabs (FLOSEAI-625).
 */
function AnalysisPage({
  recommendations,
  t,
}: Readonly<{ recommendations: InstagramRecommendationsView; t: (key: string) => string }>) {
  const { clientProfile, contentIdeas, proposals } = recommendations;

  return (
    <VStack spacing="md">
      {clientProfile && <ClientProfileSection profile={clientProfile} t={t} />}

      {contentIdeas.length > 0 && (
        <VStack spacing="xs">
          <SectionLabel
            icon={<Lightbulb size={12} color={colors.accent.amber[400]} strokeWidth={1.8} />}
            label={t('leads.instagramContent.contentIdeas')}
          />
          {contentIdeas.map((idea) => (
            <EvidencedBullet key={idea.recommendation} item={idea} t={t} />
          ))}
        </VStack>
      )}

      {proposals.length > 0 && (
        <VStack spacing="xs">
          <SectionLabel
            icon={<Handshake size={12} color={colors.status.success.main} strokeWidth={1.8} />}
            label={t('leads.instagramContent.proposals')}
          />
          {proposals.map((proposal) => (
            <EvidencedBullet key={proposal.recommendation} item={proposal} t={t} />
          ))}
        </VStack>
      )}
    </VStack>
  );
}

/**
 * The audience leads, and the reasoning sits under it. Confidence is rendered as
 * a badge rather than buried in prose because "Low" is the field a rep most needs
 * to see before quoting this profile back at anyone.
 */
function ClientProfileSection({
  profile,
  t,
}: Readonly<{ profile: ClientProfileView; t: (key: string) => string }>) {
  const confidenceColor =
    profile.confidence === 'High'
      ? 'green'
      : profile.confidence === 'Medium'
        ? 'amber'
        : ('neutral' as const);

  return (
    <VStack spacing="xs">
      <HStack spacing="xs" align="center" justify="between">
        <SectionLabel
          icon={<Users size={12} color={colors.accent.blue[400]} strokeWidth={1.8} />}
          label={t('leads.instagramContent.clientProfile')}
        />
        {profile.confidence && (
          <Badge size="sm" color={confidenceColor}>
            {t(`leads.instagramContent.confidence.${profile.confidence.toLowerCase()}`)}
          </Badge>
        )}
      </HStack>

      <Text variant="bodySmall" color={colors.text.secondary} style={styles.body}>
        {profile.audience}
      </Text>

      {profile.evidence && (
        <EvidenceLine label={t('leads.instagramContent.evidence')} text={profile.evidence} />
      )}

      {profile.priceSignals && (
        <EvidenceLine
          label={t('leads.instagramContent.priceSignals')}
          text={profile.priceSignals}
        />
      )}
    </VStack>
  );
}

/**
 * A recommendation with the observation it came from underneath. The evidence is
 * the reason this tab is worth reading: advice a rep cannot source is advice they
 * cannot repeat on a call.
 */
function EvidencedBullet({
  item,
  t,
}: Readonly<{ item: RecommendationView; t: (key: string) => string }>) {
  return (
    <VStack spacing="xxs">
      <Bullet text={item.recommendation} />
      {item.evidence && (
        <EvidenceLine label={t('leads.instagramContent.evidence')} text={item.evidence} />
      )}
    </VStack>
  );
}

function EvidenceLine({ label, text }: Readonly<{ label: string; text: string }>) {
  return (
    <Text variant="caption" color={colors.text.tertiary} style={styles.evidence}>
      {`${label}: ${text}`}
    </Text>
  );
}

function SectionLabel({ icon, label }: Readonly<{ icon: React.ReactNode; label: string }>) {
  return (
    <HStack spacing="xs" align="center">
      {icon}
      <Text variant="caption" color={colors.text.tertiary} style={styles.sectionLabel}>
        {label}
      </Text>
    </HStack>
  );
}

function Bullet({ text }: Readonly<{ text: string }>) {
  return (
    <HStack spacing="xs" align="start">
      <Text variant="bodySmall" color={colors.text.tertiary}>
        {'•'}
      </Text>
      <Text variant="bodySmall" color={colors.text.secondary} style={styles.bullet}>
        {text}
      </Text>
    </HStack>
  );
}

function OpenLink({ url, label }: Readonly<{ url: string; label: string }>) {
  return (
    <Pressable
      onPress={() => openUrl(url)}
      accessibilityRole="link"
      accessibilityLabel={label}
      hitSlop={6}
    >
      <HStack spacing="xxs" align="center">
        <ExternalLink size={12} color={colors.text.interactive} strokeWidth={1.8} />
        <Text variant="caption" color={colors.text.interactive}>
          {label}
        </Text>
      </HStack>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  semibold: { fontWeight: '600' },
  counter: { fontVariant: ['tabular-nums'] },
  body: { lineHeight: 20 },
  transcript: { lineHeight: 18, fontStyle: 'italic' },
  wrap: { flexWrap: 'wrap' },
  sectionLabel: { fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6 },
  bullet: { flex: 1, lineHeight: 19 },
  evidence: { lineHeight: 16, paddingLeft: 14, fontStyle: 'italic' },
  media: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    backgroundColor: colors.background.secondary,
  },
  dot: { width: 6, height: 6, borderRadius: 3 },
  dotActive: { backgroundColor: colors.primary[500] },
  dotIdle: { backgroundColor: colors.border.subtle },
});
